import { useEffect, useState } from "react";
import { getWalletConnectProjectId } from "../lib/runtimeConfig";
import {
  discoverProviders,
  listProviders,
  onProvidersChanged,
  type Eip6963ProviderDetail,
} from "../lib/wallet";
import {
  WALLETCONNECT_HINT,
  WALLETCONNECT_ICON,
  WALLETCONNECT_NAME,
  WALLETCONNECT_RDNS,
} from "../lib/walletConnect";

/**
 * Subscribes to EIP-6963 discovery: seeds from whatever announced before this
 * component mounted, re-asks with `eip6963:requestProvider`, and keeps
 * listening because wallets can announce late (slow extension boot).
 */
export function useWalletProviders(): Eip6963ProviderDetail[] {
  const [providers, setProviders] = useState<Eip6963ProviderDetail[]>(() => listProviders());

  useEffect(() => {
    let alive = true;
    const off = onProvidersChanged(() => {
      if (alive) setProviders(listProviders());
    });
    void discoverProviders().then((found) => {
      if (alive) setProviders(found);
    });
    return () => {
      alive = false;
      off();
    };
  }, []);

  return providers;
}

/** One row of the chooser: an announced wallet, or WalletConnect. */
export interface WalletOption {
  rdns: string;
  name: string;
  /** `data:` URI — EIP-6963 guarantees it, and the WC mark is inline. */
  icon: string;
  /** Optional right-aligned caption ("Scan with your phone"). */
  hint?: string;
}

/**
 * The announced EIP-6963 wallets, plus WalletConnect only with a project id:
 * without one, a button would hang on a relay that answers 401.
 */
export function walletOptions(providers: Eip6963ProviderDetail[]): WalletOption[] {
  const options: WalletOption[] = providers.map((p) => p.info);
  if (getWalletConnectProjectId() !== null) {
    options.push({
      rdns: WALLETCONNECT_RDNS,
      name: WALLETCONNECT_NAME,
      icon: WALLETCONNECT_ICON,
      hint: WALLETCONNECT_HINT,
    });
  }
  return options;
}

interface WalletPickerProps {
  options: WalletOption[];
  /** rdns of the pinned wallet, marked as the current choice. */
  selectedRdns: string | null;
  /**
   * Called with the chosen rdns. Must start the connect synchronously — this
   * click is the user gesture the wallet popup depends on.
   */
  onPick: (rdns: string) => void;
  disabled?: boolean;
  label?: string;
}

/**
 * Every icon is a `data:` URI, so the picker fetches nothing and cannot leak the
 * visit or break CSP. The WalletConnect modal, once open, does fetch logos from Reown.
 */
export function WalletPicker({
  options,
  selectedRdns,
  onPick,
  disabled = false,
  label = "Choose a wallet",
}: WalletPickerProps) {
  return (
    <div className="wallet-picker" role="group" aria-label={label}>
      <span className="wallet-picker-label">{label}</span>
      {options.map((option) => (
        <button
          key={option.rdns}
          type="button"
          className={`wallet-picker-option${
            option.rdns === selectedRdns ? " wallet-picker-option-selected" : ""
          }`}
          aria-pressed={option.rdns === selectedRdns}
          disabled={disabled}
          onClick={() => onPick(option.rdns)}
        >
          {option.icon !== "" && (
            <img className="wallet-picker-icon" src={option.icon} alt="" width={20} height={20} />
          )}
          <span className="wallet-picker-name">{option.name}</span>
          {option.hint !== undefined && <span className="wallet-picker-hint">{option.hint}</span>}
        </button>
      ))}
    </div>
  );
}
